export type TDcfScenarioParams = {
	purchase: number;
	sale: number;
	wacc: number;
	coe: number;
	numQuarters: number;
	ltv: number;
	baseRate: number;
	margin: number;
	opex: number;
	earnings: number;
	balloon: number;
	onHire: number;
	operatingDays: number;
};

export type TDcfLiveMetrics = {
	projectDcfValue: number;
	equityDcfValue: number;
	projectIrr: number | null;
	equityIrr: number | null;
};

// Calendar days per quarter starting from the current quarter, built once at module load
const quarterDays = (() => {
	const cache: number[] = [];
	const now = new Date();
	let q = Math.floor(now.getMonth() / 3) + 1;
	let y = now.getFullYear();
	for (let k = 0; k < 120; k++) {
		const m = (q - 1) * 3;
		cache.push(Math.round((new Date(y, m + 3, 0).getTime() - new Date(y, m, 1).getTime()) / 86400000) + 1);
		if (++q > 4) { q = 1; y++; }
	}
	return cache;
})();

// Scales an annual day count (e.g. onHire or operatingDays) to actual days in quarter i
function scaledDaysInQuarter(annualDays: number, i: number): number {
	return (annualDays / 365) * quarterDays[i];
}

// Net revenue from vessel operations in quarter i ($ Mn)
function quarterlyNetRevenue(params: TDcfScenarioParams, i: number): number {
	return (params.earnings * scaledDaysInQuarter(params.onHire, i) - params.opex * scaledDaysInQuarter(params.operatingDays, i)) / 1000;
}

function discountFactor(annualRate: number, quarterIndex: number): number {
	return 1 / Math.pow(1 + annualRate, quarterIndex / 4);
}

// Binary search for a root of fn in [lo, hi]; returns null if no sign change found
function bisect(fn: (x: number) => number, lo: number, hi: number): number | null {
	if (Math.sign(fn(lo)) === Math.sign(fn(hi))) return null;
	for (let i = 0; i < 200; i++) {
		const mid = (lo + hi) / 2;
		if (fn(mid) > 0) lo = mid; else hi = mid;
		if (hi - lo < 1e-7) break;
	}
	return (lo + hi) / 2;
}

export function projectDCF(params: TDcfScenarioParams): number {
	let pv = discountFactor(params.wacc, params.numQuarters) * params.sale;
	for (let t = 1; t <= params.numQuarters; t++) {
		pv += quarterlyNetRevenue(params, t - 1) * discountFactor(params.wacc, t);
	}
	return pv;
}

export function equityDCF(params: TDcfScenarioParams): number {
	const quarterlyInterestRate = (params.baseRate + params.margin) / 4;
	const { numQuarters, ltv: ltvRatio, balloon: balloonValue } = params;
	let sumDiscountFactors = 0, sumWeightedDiscountFactors = 0, pvNetOperatingCF = 0;
	for (let t = 1; t <= numQuarters; t++) {
		const df = discountFactor(params.coe, t);
		sumDiscountFactors += df;
		sumWeightedDiscountFactors += (t - 1) * df;
		pvNetOperatingCF += quarterlyNetRevenue(params, t - 1) * df;
	}
	const terminalDiscountFactor = discountFactor(params.coe, numQuarters);
	// Solve analytically: equityDCF = equityConstantTerm / equityPriceCoeff
	const equityPriceCoeff = (1 - ltvRatio) + quarterlyInterestRate * ltvRatio * sumDiscountFactors - quarterlyInterestRate * ltvRatio * sumWeightedDiscountFactors / numQuarters + ltvRatio * sumDiscountFactors / numQuarters;
	const equityConstantTerm = pvNetOperatingCF + (params.sale - balloonValue) * terminalDiscountFactor - quarterlyInterestRate * balloonValue * sumWeightedDiscountFactors / numQuarters + balloonValue * sumDiscountFactors / numQuarters;
	return equityConstantTerm / equityPriceCoeff;
}

export function projectIRR(params: TDcfScenarioParams): number | null {
	return bisect((r) => {
		let npv = -params.purchase + discountFactor(r, params.numQuarters) * params.sale;
		for (let t = 1; t <= params.numQuarters; t++) {
			npv += quarterlyNetRevenue(params, t - 1) * discountFactor(r, t);
		}
		return npv;
	}, -0.5, 5);
}

export function equityIRR(params: TDcfScenarioParams): number | null {
	const loanAmount = params.ltv * params.purchase;
	const principalRepayment = (loanAmount - params.balloon) / params.numQuarters;
	const annualInterestRate = params.baseRate + params.margin;
	return bisect((r) => {
		let npv = -(1 - params.ltv) * params.purchase;
		let outstandingBalance = loanAmount;
		for (let t = 1; t <= params.numQuarters; t++) {
			const terminalProceeds = t === params.numQuarters ? params.sale - params.balloon : 0;
			const cf = quarterlyNetRevenue(params, t - 1) - outstandingBalance * annualInterestRate / 4 - principalRepayment + terminalProceeds;
			npv += cf * discountFactor(r, t);
			outstandingBalance -= principalRepayment;
		}
		return npv;
	}, -0.9, 5);
}

export function backCalcEarnings(params: TDcfScenarioParams, tableDCF: number): number {
	const pvSale = discountFactor(params.wacc, params.numQuarters) * params.sale;
	let pvWeightedOnHire = 0, pvWeightedOperating = 0;
	for (let t = 1; t <= params.numQuarters; t++) {
		const df = discountFactor(params.wacc, t);
		pvWeightedOnHire += scaledDaysInQuarter(params.onHire, t - 1) / 1000 * df;
		pvWeightedOperating += scaledDaysInQuarter(params.operatingDays, t - 1) / 1000 * df;
	}
	if (pvWeightedOnHire === 0) return 0;
	return (tableDCF - pvSale + params.opex * pvWeightedOperating) / pvWeightedOnHire;
}

export function computeLiveMetrics(params: TDcfScenarioParams): TDcfLiveMetrics {
	return {
		projectDcfValue: projectDCF(params),
		equityDcfValue: equityDCF(params),
		projectIrr: projectIRR(params),
		equityIrr: equityIRR(params),
	};
}
